import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import { toast } from "react-toastify";
import useSound from "use-sound";
import api from "../../services/api";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { MdOutlineMarkChatUnread, MdNotificationsNone } from "react-icons/md";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import InstagramIcon from "@material-ui/icons/Instagram";
import FacebookIcon from "@material-ui/icons/Facebook";

import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { subscribeToPush } from "../../services/pushNotification";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import Favicon from "react-favicon";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const defaultLogoFavicon = "/favicon.ico";

const isTruthy = (val) => {
	if (typeof val === "string") {
		return ["true", "enabled", "enable", "1", "yes", "sim"].includes(val.trim().toLowerCase());
	}

	return val === true || val === 1;
};

const isGroupNotificationTicket = (ticket) => (
	isTruthy(ticket?.isGroup) ||
	ticket?.status === "group" ||
	isTruthy(ticket?.contact?.isGroup) ||
	String(ticket?.contact?.remoteJid || ticket?.remoteJid || "").includes("@g.us")
);

const getQueueAcceptanceState = (ticket) => {
	const ticketQueueId = Number(ticket?.queueId ?? ticket?.queue?.id);
	const connectionQueueId = Number(ticket?.whatsapp?.sendIdQueue);

	if (Number.isInteger(ticketQueueId) && ticketQueueId > 0) {
		return "kept";
	}

	if (Number.isInteger(connectionQueueId) && connectionQueueId > 0) {
		return "inherited";
	}

	return null;
};

const useStyles = makeStyles(theme => ({
	popoverPaper: {
		width: "100%",
		maxWidth: 380,
		borderRadius: 16,
		overflow: "hidden",
		boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 4px 20px rgba(0,0,0,0.08)",
		border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
		[theme.breakpoints.down("sm")]: {
			maxWidth: 320,
		},
	},
	badgeCustom: {
		backgroundColor: "#ef4444 !important",
		color: "#fff",
		fontSize: 11,
		fontWeight: 700,
		minWidth: 20,
		height: 20,
		borderRadius: 10,
		padding: "0 5px",
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "16px 20px 12px",
		borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
		background: theme.palette.type === "dark"
			? "linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%)"
			: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
	},
	headerTitle: {
		fontSize: 16,
		fontWeight: 700,
		color: theme.palette.text.primary,
		letterSpacing: "-0.01em",
	},
	headerBadge: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: 28,
		height: 24,
		borderRadius: 12,
		backgroundColor: "#ef4444",
		color: "#fff",
		fontSize: 12,
		fontWeight: 700,
		padding: "0 8px",
	},
	listContainer: {
		overflowY: "auto",
		maxHeight: 400,
		padding: "4px 0",
		background: theme.palette.type === "dark" ? "#1a1a2e" : "#fff",
		"&::-webkit-scrollbar": {
			width: 5,
		},
		"&::-webkit-scrollbar-thumb": {
			borderRadius: 10,
			background: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
		},
	},
	notificationItem: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "10px 16px",
		cursor: "pointer",
		transition: "background 0.15s ease",
		borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
		"&:hover": {
			background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
		},
		"&:last-child": {
			borderBottom: "none",
		},
	},
	queueBar: {
		width: 3,
		minHeight: 40,
		borderRadius: 4,
		flexShrink: 0,
		alignSelf: "stretch",
	},
	avatarWrapper: {
		position: "relative",
		flexShrink: 0,
	},
	avatar: {
		width: 40,
		height: 40,
		fontSize: 15,
		fontWeight: 600,
		backgroundColor: theme.palette.type === "dark" ? "#374151" : "#e2e8f0",
		color: theme.palette.type === "dark" ? "#d1d5db" : "#64748b",
	},
	channelIcon: {
		position: "absolute",
		bottom: -2,
		right: -2,
		width: 18,
		height: 18,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: theme.palette.type === "dark" ? "#1a1a2e" : "#fff",
		border: `1.5px solid ${theme.palette.type === "dark" ? "#1a1a2e" : "#fff"}`,
	},
	contentWrapper: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	topRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
	},
	contactName: {
		fontSize: 13,
		fontWeight: 600,
		color: theme.palette.text.primary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		flex: 1,
	},
	timestamp: {
		fontSize: 11,
		color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
		flexShrink: 0,
		fontWeight: 500,
	},
	bottomRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
	},
	lastMessage: {
		fontSize: 12,
		color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		flex: 1,
		lineHeight: 1.4,
	},
	unreadBadge: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: 20,
		height: 20,
		borderRadius: 10,
		backgroundColor: "#22c55e",
		color: "#fff",
		fontSize: 11,
		fontWeight: 700,
		padding: "0 5px",
		flexShrink: 0,
	},
	emptyState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: "40px 20px",
		gap: 12,
	},
	emptyIcon: {
		color: theme.palette.type === "dark" ? "#374151" : "#cbd5e1",
		fontSize: 48,
	},
	emptyText: {
		fontSize: 14,
		color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
		fontWeight: 500,
	},
}));

const ChannelIndicator = ({ channel }) => {
	if (channel === "whatsapp") return <WhatsAppIcon style={{ fontSize: 13, color: "#25D366" }} />;
	if (channel === "instagram") return <InstagramIcon style={{ fontSize: 13, color: "#e1306c" }} />;
	if (channel === "facebook") return <FacebookIcon style={{ fontSize: 13, color: "#3b5998" }} />;
	return null;
};

const formatTimestamp = (dateStr) => {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	if (isSameDay(date, new Date())) {
		return format(date, "HH:mm");
	}
	return format(date, "dd/MM");
};

const NotificationsPopOver = (volume) => {
	const classes = useStyles();
	const theme = useTheme();

	const history = useHistory();
	const { user, socket } = useContext(AuthContext);
	const { queues } = user;
	const { setTabOpen } = useContext(TicketsContext);

	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const queueIds = useMemo(() => (
		(queues || [])
			.map(queue => Number(queue.id))
			.filter(queueId => Number.isInteger(queueId) && queueId > 0)
	), [queues]);

	const allowedWhatsappIds = useMemo(() => {
		const userWhatsappIds = (user?.whatsapps || [])
			.map(whatsapp => Number(whatsapp.id))
			.filter(whatsappId => Number.isInteger(whatsappId) && whatsappId > 0);
		const fallbackWhatsappId = Number(user?.whatsappId);

		return userWhatsappIds.length > 0
			? userWhatsappIds
			: (Number.isInteger(fallbackWhatsappId) && fallbackWhatsappId > 0 ? [fallbackWhatsappId] : []);
	}, [user]);

	const { get: getSetting } = useCompanySettings();

	const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
	const [showNotificationPending, setShowNotificationPending] = useState(false);
	const [showGroupNotification, setShowGroupNotification] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({
		withUnreadMessages: "true",
		forceSearch: showNotificationPending ? "pending-on" : "pending-off"
	});

	const [play] = useSound(alertSound, volume);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);
	const notificationsRef = useRef([]);
	const lastAlertedUnreadRef = useRef({});

	const normalizeNotificationTicket = useCallback((ticket) => {
		const shouldBlurMessages = ticket?.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

		if (!shouldBlurMessages) {
			return ticket;
		}

		return {
			...ticket,
			lastMessage: i18n.t("notifications.messageHidden")
		};
	}, [user]);

	useEffect(() => {
		notificationsRef.current = notifications;
	}, [notifications]);

	const canShowTicketNotification = useCallback((ticket) => {
		if (!ticket) return false;

		const isGroupTicket = isGroupNotificationTicket(ticket);
		const isPendingTicket = ticket.status === "pending";
		const ticketWhatsappId = Number(ticket.whatsappId ?? ticket?.whatsapp?.id);
		const ticketQueueId = Number(ticket.queueId ?? ticket?.queue?.id);
		const ticketUserId = Number(ticket.userId ?? ticket?.user?.id);
		const currentUserId = Number(user?.id);

		// --- Group ticket rules ---
		if (isGroupTicket) {
			if (!showGroupNotification) return false;

			// Filter by allowed connections (if user has connections configured)
			if (
				allowedWhatsappIds.length > 0 &&
				Number.isInteger(ticketWhatsappId) &&
				ticketWhatsappId > 0 &&
				!allowedWhatsappIds.includes(ticketWhatsappId)
			) {
				return false;
			}

			// Groups pass queue check: show if queue matches OR ticket has no queue
			if (Number.isInteger(ticketQueueId) && ticketQueueId > 0) {
				if (queueIds.length > 0 && !queueIds.includes(ticketQueueId)) return false;
			}
			// Groups without queue are allowed when showGroupNotification is true

			return true;
		}

		// --- Non-group ticket rules ---

		// Skip special statuses
		if (["closed", "lgpd", "nps", "chatbot"].includes(ticket.status)) return false;

		// Pending tickets: respect setting, do NOT filter by userId
		if (isPendingTicket) {
			if (!showNotificationPending) return false;
			// Queue check for pending
			if (Number.isInteger(ticketQueueId) && ticketQueueId > 0) {
				if (queueIds.length > 0 && !queueIds.includes(ticketQueueId)) return false;
			}
			// Pending without queue is always visible when setting is active
			return true;
		}

		// Open tickets: filter by userId
		if (
			Number.isInteger(ticketUserId) &&
			ticketUserId > 0 &&
			Number.isInteger(currentUserId) &&
			ticketUserId !== currentUserId
		) {
			return false;
		}

		// Queue check for open
		if (Number.isInteger(ticketQueueId) && ticketQueueId > 0) {
			if (queueIds.length > 0 && !queueIds.includes(ticketQueueId)) return false;
			return true;
		}

		return showTicketWithoutQueue;
	}, [allowedWhatsappIds, queueIds, showGroupNotification, showNotificationPending, showTicketWithoutQueue, user]);

	const getNotificationMessageBody = useCallback((ticket, message) => {
		const shouldBlurMessages = ticket?.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

		if (shouldBlurMessages) {
			return i18n.t("notifications.messageHidden");
		}

		return message?.body || ticket?.lastMessage || i18n.t("tickets.notification.message");
	}, [user]);

	const shouldAlertFromTicketUpdate = useCallback((ticket) => {
		const ticketId = Number(ticket?.id);
		const isPendingTicket = ticket?.status === "pending" && !isGroupNotificationTicket(ticket);
		const nextUnreadMessages = Number(ticket?.unreadMessages || 0);
		const previousTicket = notificationsRef.current.find(currentTicket => currentTicket.id === ticketId);

		if (isPendingTicket && !previousTicket && canShowTicketNotification(ticket)) {
			return true;
		}

		if (!Number.isInteger(ticketId) || ticketId <= 0 || nextUnreadMessages <= 0) {
			return false;
		}

		const previousUnreadMessages = Number(previousTicket?.unreadMessages || 0);
		const lastAlertedUnreadMessages = Number(lastAlertedUnreadRef.current[ticketId] || 0);

		return nextUnreadMessages > previousUnreadMessages && nextUnreadMessages > lastAlertedUnreadMessages;
	}, [canShowTicketNotification]);

	const handleAcceptTicket = useCallback(async (ticket) => {
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: "open",
				userId: user?.id,
			});
			const tabTarget = isGroupNotificationTicket(ticket) ? "group" : "open";
			setTabOpen(tabTarget);
			historyRef.current.push(`/tickets/${ticket.uuid}`);
		} catch (err) {
			toastError(err);
		}
	}, [user, setTabOpen]);

	const showTicketActivityNotification = useCallback(({ ticket, contact, message, dedupeKey }) => {
		if (!ticket || !dedupeKey || toast.isActive(dedupeKey)) return;

		const ticketId = Number(ticket.id);
		const unreadMessages = Number(ticket.unreadMessages || 0);

		if (Number.isInteger(ticketId) && ticketId > 0 && unreadMessages > 0) {
			lastAlertedUnreadRef.current[ticketId] = unreadMessages;
		} else if (Number.isInteger(ticketId) && ticketId > 0 && ticket.status === "pending") {
			lastAlertedUnreadRef.current[ticketId] = Math.max(Number(lastAlertedUnreadRef.current[ticketId] || 0), 1);
		}

		const messageBody = getNotificationMessageBody(ticket, message);
		const messagePreview = messageBody.length > 60 ? `${messageBody.substring(0, 60)}...` : messageBody;
		const contactName = contact?.name || ticket?.contact?.name || "Nova mensagem";
		const isGroup = isGroupNotificationTicket(ticket);
		const status = isGroup ? "group" : ticket.status;
		const statusLabel = isGroup ? "Grupo" : ticket.status === "pending" ? "Aguardando" : "";
		const isPending = ticket.status === "pending" && !isGroup;
		const queueAcceptanceState = getQueueAcceptanceState(ticket);
		const queueBadgeLabel = queueAcceptanceState ? i18n.t(`hardcoded.queueAcceptance.${queueAcceptanceState}`) : null;
		const queueName = ticket?.queue?.name || ticket?.whatsapp?.queue?.name || null;
		const queueBadgeStyles = queueAcceptanceState === "kept"
			? { color: "#065f46", backgroundColor: "#dcfce7", borderColor: "#86efac" }
			: { color: "#1d4ed8", backgroundColor: "#dbeafe", borderColor: "#93c5fd" };

		toast(
			({ closeToast }) => (
				<div style={{ padding: "4px 0" }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 12,
							cursor: "pointer",
						}}
						onClick={() => {
							setTabOpen(status);
							historyRef.current.push(`/tickets/${ticket.uuid}`);
							closeToast();
						}}
					>
						<div style={{
							width: 40,
							height: 40,
							borderRadius: "50%",
							background: "#e2e8f0",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
							overflow: "hidden",
						}}>
							{contact?.urlPicture || ticket?.contact?.urlPicture ? (
								<img
									src={contact?.urlPicture || ticket?.contact?.urlPicture}
									alt=""
									style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
								/>
							) : (
								<span style={{ fontSize: 16, fontWeight: 600, color: "#64748b" }}>
									{contactName.charAt(0).toUpperCase()}
								</span>
							)}
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
								<strong style={{ fontSize: 14, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{contactName}
								</strong>
								{statusLabel && (
									<span style={{
										fontSize: 10,
										fontWeight: 600,
										color: "#fff",
										backgroundColor: isGroup ? "#6366f1" : "#f59e0b",
										borderRadius: 4,
										padding: "1px 6px",
										flexShrink: 0,
									}}>
										{statusLabel}
									</span>
								)}
							</div>
							{queueBadgeLabel && (
								<div style={{ marginTop: 6 }}>
									<span style={{
										display: "inline-flex",
										alignItems: "center",
										padding: "2px 8px",
										borderRadius: 999,
										fontSize: 11,
										fontWeight: 600,
										border: `1px solid ${queueBadgeStyles.borderColor}`,
										...queueBadgeStyles,
									}}>
										{queueName ? `${queueBadgeLabel} • ${queueName}` : queueBadgeLabel}
									</span>
								</div>
							)}
							<div style={{ fontSize: 13, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
								{messagePreview || "📎 Mídia"}
							</div>
						</div>
					</div>
					{isPending && (
						<div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
							<button
								onClick={(e) => {
									e.stopPropagation();
									handleAcceptTicket(ticket);
									closeToast();
								}}
								style={{
									padding: "4px 14px",
									fontSize: 12,
									fontWeight: 600,
									borderRadius: 6,
									border: "none",
									cursor: "pointer",
									background: "#22c55e",
									color: "#fff",
								}}
							>
								Aceitar
							</button>
							<button
								onClick={(e) => {
									e.stopPropagation();
									closeToast();
								}}
								style={{
									padding: "4px 14px",
									fontSize: 12,
									fontWeight: 600,
									borderRadius: 6,
									border: "1px solid #e2e8f0",
									cursor: "pointer",
									background: "#fff",
									color: "#64748b",
								}}
							>
								Fechar
							</button>
						</div>
					)}
				</div>
			),
			{
				toastId: dedupeKey,
				autoClose: isPending ? 10000 : 6000,
				closeOnClick: false,
				pauseOnHover: true,
				draggable: true,
				style: {
					background: "#fff",
					borderRadius: 12,
					boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
					border: "1px solid rgba(0,0,0,0.06)",
					padding: "12px 16px",
				},
			}
		);

		handleBrowserNotification({ contact: contact || ticket.contact, ticket }, messageBody);
		soundAlertRef.current?.();
	}, [getNotificationMessageBody, handleAcceptTicket, handleBrowserNotification, setTabOpen]);

	const upsertNotification = useCallback((ticket) => {
		const ticketToAdd = normalizeNotificationTicket(ticket);

		setNotifications(prevState => {
			const ticketIndex = prevState.findIndex(t => t.id === ticketToAdd.id);
			if (ticketIndex !== -1) {
				const nextState = [...prevState];
				nextState[ticketIndex] = ticketToAdd;
				return nextState;
			}

			return [ticketToAdd, ...prevState];
		});

		return ticketToAdd;
	}, [normalizeNotificationTicket]);

	const removeNotification = useCallback((ticketId) => {
		setNotifications(prevState => prevState.filter(t => t.id !== ticketId));
		delete lastAlertedUnreadRef.current[ticketId];

		setDesktopNotifications(prevState => {
			prevState
				.filter(notification => notification.tag === String(ticketId))
				.forEach(notification => notification.close());

			return prevState.filter(notification => notification.tag !== String(ticketId));
		});
	}, []);

	const clearNotifications = useCallback(() => {
		setNotifications([]);

		setDesktopNotifications(prevState => {
			prevState.forEach(notification => notification.close());
			return [];
		});
	}, []);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const setting = await getSetting({
					"column": "showNotificationPending"
				});

				setShowNotificationPending(isTruthy(setting?.showNotificationPending));
				setShowTicketWithoutQueue(isTruthy(user?.allTicket));
				setShowGroupNotification(isTruthy(user?.allowGroup));
			} catch (err) {
				toastError(err);
			}
		};

		fetchSettings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission().then((permission) => {
				if (permission === "granted") {
					subscribeToPush().catch(err => console.error("[PUSH] Erro ao subscribir:", err));
				}
			});
		}
	}, [play]);

	useEffect(() => {
		const filtered = tickets
			.filter(ticket => ticket.unreadMessages > 0 && canShowTicketNotification(ticket))
			.map(ticket => normalizeNotificationTicket(ticket));

		setNotifications(filtered);
	}, [tickets, canShowTicketNotification, normalizeNotificationTicket]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const companyId = user.companyId;
		if (!user.id || !socket) return undefined;

		const onConnectNotificationsPopover = () => {
			socket.emit("joinNotification");
		};

		const onCompanyTicketNotificationsPopover = (data) => {
			if (data.action === "updateUnread" || data.action === "delete") {
				removeNotification(data.ticketId);
				return;
			}

			if (data.action === "update" && data.ticket) {
				const ticket = data.ticket;
				const isPendingTicket = ticket.status === "pending" && !isGroupNotificationTicket(ticket);

				if ((!ticket.unreadMessages && !isPendingTicket) || !canShowTicketNotification(ticket)) {
					removeNotification(ticket.id);
					return;
				}

				upsertNotification(ticket);

				const isCurrentTicketOpen = ticket.id === ticketIdRef.current && document.visibilityState === "visible";

				if (!isCurrentTicketOpen && shouldAlertFromTicketUpdate(ticket)) {
					showTicketActivityNotification({
						ticket,
						contact: ticket.contact,
						dedupeKey: `ticket-${ticket.id}-${ticket.unreadMessages}-${ticket.updatedAt || "update"}`
					});
				}
			}
		};

		const onCompanyAppMessageNotificationsPopover = (data) => {
			if (data.action !== "create" || data.message.fromMe) return;

			const ticket = data.ticket || data.message?.ticket;
			const isPendingTicket = ticket?.status === "pending" && !isGroupNotificationTicket(ticket);

			if (!ticket) return;
			if (data.message.read && !isPendingTicket) return;
			if (!canShowTicketNotification(ticket)) return;

			upsertNotification(ticket);

			const isCurrentTicketVisible =
				(data.message.ticketId === ticketIdRef.current || ticket.id === ticketIdRef.current) &&
				document.visibilityState === "visible";

			if (isCurrentTicketVisible) return;

			showTicketActivityNotification({
				ticket,
				contact: data.contact || ticket?.contact,
				message: data.message,
				dedupeKey: `msg-${data.message.id}`
			});
		};

		socket.emit("joinNotification");
		socket.on("connect", onConnectNotificationsPopover);
		socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
		socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);

		return () => {
			socket.off("connect", onConnectNotificationsPopover);
			socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
			socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
		};
	}, [user, socket, canShowTicketNotification, removeNotification, showGroupNotification, showNotificationPending, showTicketWithoutQueue, shouldAlertFromTicketUpdate, showTicketActivityNotification, upsertNotification]);

	function handleBrowserNotification(data, messageBody) {
		const { contact, ticket } = data;

		if (!("Notification" in window) || Notification.permission !== "granted") return;

		try {
			const options = {
				body: `${messageBody} - ${format(new Date(), "HH:mm")}`,
				icon: contact?.urlPicture,
				tag: String(ticket.id),
				renotify: true,
			};
			const notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact?.name || ""}`,
				options
			);

			notification.onclick = e => {
				e.preventDefault();
				window.focus();
				const status = isGroupNotificationTicket(ticket) ? "group" : ticket.status;
				setTabOpen(status);
				historyRef.current.push(`/tickets/${ticket.uuid}`);
			};

			setDesktopNotifications(prevState => {
				const notificationIndex = prevState.findIndex(
					n => n.tag === notification.tag
				);
				if (notificationIndex !== -1) {
					prevState[notificationIndex] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});
		} catch (err) {
			console.error("Browser notification error:", err);
		}
	}

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const handleSelectTicket = (ticket) => {
		const status = isGroupNotificationTicket(ticket) ? "group" : ticket.status;
		setTabOpen(status);
		history.push(`/tickets/${ticket.uuid}`);
		handleClickAway();
	};

	const getInitials = (name) => {
		if (!name) return "?";
		const parts = name.trim().split(" ");
		if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
		return parts[0][0]?.toUpperCase() || "?";
	};

	const browserNotification = () => {
		const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
		if (notifications.length > 0) {
			if (notifications.length < 21) {
				document.title = numbers.substring(notifications.length, notifications.length + 1) + " - " + (theme.appName || "...");
			} else {
				document.title = "(" + notifications.length + ")" + (theme.appName || "...");
			}
		} else {
			document.title = theme.appName || "...";
		}
		return (
			<Favicon
				animated={true}
				url={(theme?.appLogoFavicon) ? theme.appLogoFavicon : defaultLogoFavicon}
				alertCount={notifications.length}
				iconSize={195}
			/>
		);
	};

	return (
		<>
			{browserNotification()}

			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
				size="small"
				style={{ padding: 8, minWidth: 40, minHeight: 40 }}
			>
				<Badge
					overlap="rectangular"
					badgeContent={notifications.length}
					max={99}
					color="error"
					anchorOrigin={{ vertical: "top", horizontal: "right" }}
				classes={{ badge: classes.badgeCustom }}
				invisible={notifications.length === 0}
				>
					<MdOutlineMarkChatUnread size={24} color="#6b7280" />
				</Badge>
			</IconButton>

			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
			{/* Header */}
				<Box className={classes.header}>
					<Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<Typography className={classes.headerTitle}>
							{i18n.t("notifications.title")}
						</Typography>
						{notifications.length > 0 && (
							<span className={classes.headerBadge}>
								{notifications.length}
							</span>
						)}
					</Box>
					{notifications.length > 0 && (
						<span
							onClick={clearNotifications}
							style={{
								fontSize: 13,
								fontWeight: 500,
								color: "#6b7280",
								cursor: "pointer",
								userSelect: "none",
								padding: "2px 6px",
								borderRadius: 6,
								transition: "background 0.15s",
							}}
							onMouseEnter={(e) => e.target.style.background = "rgba(0,0,0,0.05)"}
							onMouseLeave={(e) => e.target.style.background = "transparent"}
						>
							{i18n.t("notifications.clearAll")}
						</span>
					)}
				</Box>

				{/* List */}
				<Box className={classes.listContainer}>
					{notifications.length === 0 ? (
						<Box className={classes.emptyState}>
							<MdNotificationsNone className={classes.emptyIcon} />
							<Typography className={classes.emptyText}>
								{i18n.t("notifications.noTickets")}
							</Typography>
						</Box>
					) : (
						notifications.map(ticket => (
							<Box
								key={ticket.id}
								className={classes.notificationItem}
								onClick={() => handleSelectTicket(ticket)}
							>
								{/* Queue color bar */}
								<Box
									className={classes.queueBar}
									style={{
										backgroundColor: ticket.queue?.color || "#94a3b8",
									}}
								/>

								{/* Avatar + channel icon */}
								<Box className={classes.avatarWrapper}>
									<Avatar
										className={classes.avatar}
										src={ticket.contact?.urlPicture}
										alt={ticket.contact?.name}
									>
										{getInitials(ticket.contact?.name)}
									</Avatar>
									{ticket.channel && (
										<Box className={classes.channelIcon}>
											<ChannelIndicator channel={ticket.channel} />
										</Box>
									)}
								</Box>

								{/* Content */}
								<Box className={classes.contentWrapper}>
									<Box className={classes.topRow}>
										<Typography className={classes.contactName}>
											{ticket.contact?.name || "#" + ticket.id}
										</Typography>
										<Typography className={classes.timestamp}>
											{formatTimestamp(ticket.updatedAt)}
										</Typography>
									</Box>
									<Box className={classes.bottomRow}>
										<Typography className={classes.lastMessage}>
											{ticket.lastMessage || "..."}
										</Typography>
										{ticket.unreadMessages > 0 && (
											<span className={classes.unreadBadge}>
												{ticket.unreadMessages}
											</span>
										)}
									</Box>
								</Box>
							</Box>
						))
					)}
				</Box>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
